import { DataProps } from "..";
import Image from "next/image";
import Breadcrumbs from "@/components/Breadcrumbs/Breadcrumbs";
import CondensedTaxiForm from "@/components/TaxiForm/variants/Condensed";
import decodeMalformedText from "@/utils/decodeMalformedText";
import HeroStats from "@/components/HeroStats";
import { DegreeLeadFormContextTestResult } from "@/monarch/rules/degreeLeadFormContext/types";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import LeadFormContext from "../components/LeadFormContext";
import MonarchResolver from "@/monarch/MonarchResolver";
import useTranslations from "@/i18n/useTranslations";

export function FormProductHeroCombined({
  brandImage,
  courseDetails,
  courseImage,
  partner,
  path,
  productType,
  subheader,
  taxiForm,
  taxiFormData,
  title,
  statsVariant = "default",
  hasUsNewsLogo = false,
  colorBarHexValue,
}: DataProps) {
  const t = useTranslations("Common");
  const gapClasses = `flex flex-col lg:flex-row items-center lg:items-start gap-6 p-10 bg-white
    ${statsVariant === "dark-logo-nogap" ? "mt-8 rounded-t-xl" : "my-8 rounded-xl"}`;

  return (
    <div className="justify-center relative bg-opacity-65 bg-black py-6 md:py-16">
      <Image
        src={courseImage || ""}
        layout="fill"
        alt={t("backgroundImageAlt")}
        className="absolute z-[-1] object-cover object-center"
      />
      <div className="container">
        <Breadcrumbs variant="light" path={path || ""} pageName={title} />
      </div>
      <div className="container">
        <div className={gapClasses}>
          <div className="flex flex-col">
            <div className="w-fit mb-4 mx-auto lg:mx-0 bg-primary p-4">
              <Image
                className="h-[73px] w-auto object-contain"
                width={243}
                height={73}
                src={brandImage || ""}
                alt={title || ""}
              />
            </div>
            <div className="flex items-center">
              <div>
                {partner && productType && (
                  <p className="md:text-lg p-0 mb-4">
                    {productType} from {partner}
                  </p>
                )}
                <h1 className="text-primary font-black text-3xl md:text-4xl my-0 pt-0">
                  {decodeMalformedText(title)}
                </h1>
                <div className="pt-6 max-w-[984px] text-lg leading-9">
                  <p className="md:text-lg">{decodeMalformedText(subheader)}</p>
                </div>
              </div>
            </div>
          </div>
          <MonarchResolver
            experimentKey="useDegreeLeadFormContext"
            variants={{
              control: (
                <div id={taxiForm?.id}>
                  {taxiForm && (
                    <CondensedTaxiForm
                      {...taxiForm}
                      taxiFormData={taxiFormData}
                      variant="condensed"
                    />
                  )}
                </div>
              ),
              showContext: (
                <MonarchDataConsumer<DegreeLeadFormContextTestResult>
                  experimentKey="useDegreeLeadFormContext"
                  render={({ data }) => {
                    return (
                      <div className="flex flex-col gap-4">
                        <LeadFormContext
                          showContext={
                            data?.leadFormContext?.showContext ?? false
                          }
                          showTitle={data?.leadFormContext?.showTitle ?? false}
                          title={data?.leadFormContext?.title ?? ""}
                          content={data?.leadFormContext?.content ?? ""}
                          icon={data?.leadFormContext?.icon ?? ""}
                          position={data?.leadFormContext?.position ?? "above"}
                          targetPosition="above"
                        />
                        <div id={taxiForm?.id}>
                          {taxiForm && (
                            <CondensedTaxiForm
                              {...taxiForm}
                              taxiFormData={taxiFormData}
                              variant="condensed"
                            />
                          )}
                        </div>
                        <LeadFormContext
                          targetPosition="below"
                          showContext={
                            data?.leadFormContext?.showContext ?? false
                          }
                          showTitle={data?.leadFormContext?.showTitle ?? false}
                          title={data?.leadFormContext?.title ?? ""}
                          content={data?.leadFormContext?.content ?? ""}
                          icon={data?.leadFormContext?.icon ?? ""}
                          position={data?.leadFormContext?.position ?? "below"}
                        />
                      </div>
                    );
                  }}
                />
              ),
            }}
            fallbackVariant="control"
          />
        </div>
      </div>

      <HeroStats
        hasUsNewsLogo={hasUsNewsLogo ?? false}
        variant={statsVariant}
        courseDetails={courseDetails}
        colorBarHexValue={colorBarHexValue}
      />
    </div>
  );
}
